import React, {
  Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle,
} from 'react';
import {
  View, StyleSheet, ScrollView, Text, Pressable,
} from 'react-native';
import * as Device from 'expo-device';
import { useSelector } from 'react-redux';
import isMobile from '../../../components/IsMobile';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import getMyTickets from '../../../requests/Tickets/GetMyTickets';
import { logEvent } from '../../../components/LogEvent';
import { PressableState } from '../../../components/PressableStyled';

// eslint-disable-next-line no-shadow
export enum OpenTicketsShown {
  // eslint-disable-next-line no-unused-vars
  OPEN = 'open', OPEN_OVERDUE = 'openOverdue', OPEN_ASSIGNED_TO_ME = 'openAssignedToMe',
  // eslint-disable-next-line no-unused-vars
  OPEN_ASSIGNED_TO_ME_OVERDUE = 'openAssignedToMeOverdue',
}

export interface OpenTicketStatistics {
  open: number;
  openOverdue: number;
  openAssignedToMe: number;
  openAssignedToMeOverdue: number;
}

interface TicketCountWidgetProps {
  openTicketsSelection: OpenTicketsShown | null,
  setOpenTicketsSelection: Dispatch<SetStateAction<OpenTicketsShown | null>>,
  renderTabletFilterButton: () => React.ReactElement,
  counts: OpenTicketStatistics,
  setCounts: Dispatch<SetStateAction<OpenTicketStatistics>>,
}

const widgetValues = (
  total: number,
  overdue: number,
  firstPress: () => void,
  secondPress: () => void,
  firstSelected: boolean,
  secondSelected: boolean,
  firstTestID: string,
  secondTestID: string,
) => {
  let overdueColor = Colors.light.hereworksBlack400;
  if (overdue > 0) overdueColor = Colors.light.hereworksRed500;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
      <View style={{
        paddingRight: 16, borderRightWidth: 1, borderColor: Colors.light.hereworksBlue50,
      }}>
        <Pressable
          onPress={firstPress}
          style={({ hovered }: PressableState) => [
            { borderWidth: 1, borderColor: 'transparent', borderRadius: 6 },
            firstSelected && {
              backgroundColor: Colors.light.hereworksBlue50,
              borderColor: Colors.light.hereworksBlue500,
            },
            hovered && {
              backgroundColor: Colors.light.hereworksBlue50,
              borderColor: Colors.light.hereworksBlue200,
            },
          ]}
          testID={firstTestID}
        >
          <Text style={[
            styles.value, firstSelected && { color: Colors.light.hereworksBlue500 },
          ]}>
            {total}
          </Text>
        </Pressable>
      </View>
      <Pressable
        onPress={secondPress}
        style={({ hovered }: PressableState) => [
          {
            borderWidth: 1,
            borderColor: 'transparent',
            borderRadius: 6,
            marginLeft: 16,
          },
          secondSelected && {
            backgroundColor: Colors.light.hereworksRed50,
            borderColor: Colors.light.hereworksRed100,
          },
          hovered && {
            backgroundColor: Colors.light.hereworksRed50,
            borderColor: Colors.light.hereworksPink400,
          },
        ]}
        testID={secondTestID}
      >
        <Text style={[
          styles.value,
          secondSelected && { backgroundColor: Colors.light.hereworksRed50 },
          { color: overdueColor },
        ]}>
          {overdue}
        </Text>
      </Pressable>
      <Text style={styles.overdueText}>{'Overdue'}</Text>
    </View>
  );
};

const widgetValueMobile = (total, overdue = false) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
    <View>
      <Text style={[
        styles.valueMobile,
        overdue && {
          color: Colors.light.hereworksRed500,
          backgroundColor: Colors.light.hereworksRed50,
          borderColor: Colors.light.hereworksLightPink,
        },
      ]}>
        {total}
      </Text>
    </View>
  </View>
);

const TicketCountWidget = forwardRef(({
  openTicketsSelection,
  setOpenTicketsSelection,
  renderTabletFilterButton,
  counts,
  setCounts,
}: TicketCountWidgetProps, ref) => {
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const openSelected: boolean = openTicketsSelection === OpenTicketsShown.OPEN;
  const openAssignedToMeSelected: boolean = openTicketsSelection
    === OpenTicketsShown.OPEN_ASSIGNED_TO_ME;
  const openOverdueSelected: boolean = openTicketsSelection === OpenTicketsShown.OPEN_OVERDUE;
  const openAssignedToMeOverdueSelected: boolean = openTicketsSelection
    === OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE;

  useImperativeHandle(ref, () => ({ refresh }));

  const refresh: () => void = () => {
    if (!selectedBuildingId) return;
    getMyTickets(selectedBuildingId).then((res) => {
      if (!(res && !res.isError)) return;
      setCounts(res);
    });
  };

  useEffect(refresh, [selectedBuildingId]);

  const setOpenTicketsFilter: () => void = () => {
    setOpenTicketsSelection(openSelected ? null : OpenTicketsShown.OPEN);
    logEvent('Tickets', 'open_tickets');
  };

  const setOpenOverdueTicketsFilter: () => void = () => {
    logEvent('Tickets', 'overdue');
    setOpenTicketsSelection(openOverdueSelected ? null : OpenTicketsShown.OPEN_OVERDUE);
  };

  const setOpenAssignedToMeTicketsFilter: () => void = () => {
    setOpenTicketsSelection(openAssignedToMeSelected ? null : OpenTicketsShown.OPEN_ASSIGNED_TO_ME);
    logEvent('Tickets', 'assigned_to_me');
  };

  const setOpenAssignedToMeOverdueTicketsFilter: () => void = () => {
    setOpenTicketsSelection(openAssignedToMeOverdueSelected
      ? null : OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE);
    logEvent('Tickets', 'assigned_to_me_overdue');
  };

  return <>
    {isMobile ? <View style={{ flexDirection: 'row' }}>
      <ScrollView
        contentContainerStyle={styles.containerMobile}
        horizontal
      >
        <Pressable style={({ pressed }: PressableState) => [
          pressed && Style.pressed, styles.contentMobile,
          openSelected && { backgroundColor: Colors.light.hereworksDarkBlue },
        ]}
          onPress={setOpenTicketsFilter}
        >
          <Text style={[styles.titleMobile, openSelected && { color: '#fff' }]}>
            {'Open'}
          </Text>
          {widgetValueMobile(counts?.open)}
        </Pressable>
        <Pressable style={(state) => [
          state.pressed && Style.pressed, styles.contentMobile,
          openAssignedToMeSelected && { backgroundColor: Colors.light.hereworksDarkBlue }]}
          onPress={setOpenAssignedToMeTicketsFilter}>
          <Text style={[styles.titleMobile, openAssignedToMeSelected && { color: '#fff' }]}>
            {'Assigned to Me'}
          </Text>
          {widgetValueMobile(counts?.openAssignedToMe, counts?.openAssignedToMeOverdue > 0)}
        </Pressable>
        <Pressable style={(state) => [
          state.pressed && Style.pressed, styles.contentMobile,
          openOverdueSelected && { backgroundColor: Colors.light.hereworksDarkBlue }]}
          onPress={setOpenOverdueTicketsFilter}>
          <Text style={[styles.titleMobile, openOverdueSelected && { color: '#fff' }]}>
            {'Overdue'}
          </Text>
          {widgetValueMobile(counts?.openOverdue, true)}
        </Pressable>
      </ScrollView>
      {Device.deviceType === Device.DeviceType.TABLET ? renderTabletFilterButton() : null}
    </View> : <View style={styles.containerWeb}>
      <View style={Style.row}>
        <View style={styles.contentWeb}>
          <Text style={styles.title}>{'All open tickets'}</Text>
          {widgetValues(
            counts?.open,
            counts?.openOverdue,
            setOpenTicketsFilter,
            setOpenOverdueTicketsFilter,
            openSelected,
            openOverdueSelected,
            'ticket-all-open-tickets-btn',
            'ticket-all-overdue-tickets-btn',
          )}
        </View>
        <View style={[{ flex: 1, marginLeft: 16 }]}>
          <Text style={styles.title}>{'Assigned to me'}</Text>
          {widgetValues(
            counts?.openAssignedToMe,
            counts?.openAssignedToMeOverdue,
            setOpenAssignedToMeTicketsFilter,
            setOpenAssignedToMeOverdueTicketsFilter,
            openAssignedToMeSelected,
            openAssignedToMeOverdueSelected,
            'ticket-all-assigned-tickets-btn',
            'ticket-all-assigned-overdue-tickets-btn',
          )}
        </View>
      </View>
      {false && <View style={[Style.row, { marginTop: 24 }]}>
        <View style={styles.contentWeb}>
          <Text style={styles.title}>{'Filtered open tickets'}</Text>
          {widgetValues(
            counts?.open,
            counts?.openOverdue,
            setOpenTicketsFilter,
            setOpenOverdueTicketsFilter,
            openSelected,
            openOverdueSelected,
            'ticket-all-open-tickets-btn',
            'ticket-all-overdue-tickets-btn',
          )}
        </View>
        <View style={[{ flex: 1, marginLeft: 16 }]}>
          <Text style={styles.title}>{'Filtered tickets assigned to me'}</Text>
          {widgetValues(
            counts?.openAssignedToMe,
            counts?.openAssignedToMeOverdue,
            setOpenAssignedToMeTicketsFilter,
            setOpenAssignedToMeOverdueTicketsFilter,
            openAssignedToMeSelected,
            openAssignedToMeOverdueSelected,
            'ticket-all-assigned-tickets-btn',
            'ticket-all-assigned-overdue-tickets-btn',
          )}
        </View>
      </View>}
    </View>}
  </>;
});

const styles = StyleSheet.create({
  containerMobile: {
    marginTop: 8,
    marginBottom: 20,
    marginHorizontal: 8,
    paddingEnd: 40,
    alignItems: 'flex-start',
  },
  contentMobile: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 4,
    marginHorizontal: 6,
    paddingHorizontal: 20,
    paddingVertical: 8,
    marginTop: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  containerWeb: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#fff',
    marginHorizontal: 24,
    marginBottom: 16,
    marginTop: 8,
    padding: 16,
  },
  contentWeb: {
    flex: 1,
    borderRightWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
  },
  title: {
    marginBottom: 16,
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  titleMobile: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  overdueText: {
    marginLeft: 4,
    marginBottom: isMobile ? 8 : 6,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 14,
  },
  value: {
    fontSize: 30,
    lineHeight: isMobile ? undefined : 30,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  valueMobile: {
    fontSize: 12,
    paddingVertical: 1,
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Roboto_400Regular',
    marginLeft: 6,
    backgroundColor: Colors.light.hereworksWhite500,
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1,
    paddingHorizontal: 6,
    borderRadius: 10,
    textAlign: 'center',
    overflow: 'hidden',
  },
});

export default TicketCountWidget;
